import { post, route } from 'remix/routes'

export const routes = route({
	home: '/',
	landingHeroVideosApi: '/landing-hero-videos.json',
	notFoundPage: '/404',
	internalErrorPage: '/500',
	robotsTxt: '/robots.txt',
	sitemap: '/sitemap.xml',
	authMarkdown: '/auth.md',
	mcpServerCard: '/.well-known/mcp/server-card.json',
	apiCatalog: '/.well-known/api-catalog',
	agentSkillsIndex: '/.well-known/agent-skills/index.json',
	agentSkillMarkdown: '/.well-known/agent-skills/:skillId/SKILL.md',
	securityTxt: '/.well-known/security.txt',
	openaiAppsChallenge: '/.well-known/openai-apps-challenge',
	connectOauth: '/connect/oauth',
	connectSecrets: '/connect/secrets',
	integrationLogo: '/integrations/logos/:integrationSlug',
	providerMarkLogo: '/integrations/provider-marks/:slug',
	accountIntegrations: '/account/integrations',
	// More specific than `:integrationName` so a connection named `apps` still
	// resolves at `/account/integrations/apps` while OAuth apps live under
	// `/account/integrations/apps/:appSlug`.
	accountOauthAppDetail: '/account/integrations/apps/:appSlug',
	// Static `/approve` must win over `:integrationName` so a connection named
	// `approve` is not required to claim the one-click grant URL.
	accountIntegrationsApprove: '/account/integrations/approve',
	accountIntegrationDetail: '/account/integrations/:integrationName',
	accountIntegrationsApi: '/account/integrations.json',
	accountIntegrationsApiPost: post('/account/integrations.json'),
	accountMcpServers: '/account/mcp-servers',
	accountMcpServerNew: '/account/mcp-servers/new',
	// More specific than `:serverId` so favicon assets do not collide with
	// the expand-detail route.
	accountMcpServerLogo: '/account/mcp-servers/logos/:serverId',
	accountMcpServerDetail: '/account/mcp-servers/:serverId',
	accountMcpServersOauthCallback: '/account/mcp-servers/oauth/callback',
	accountMcpServersApi: '/account/mcp-servers.json',
	accountMcpServersApiPost: post('/account/mcp-servers.json'),
	// Legacy HTML package routes only redirect to their canonical `/@…` pages.
	// The JSON endpoints remain account-scoped mutation/read APIs.
	accountPackages: '/account/packages',
	accountPackageDetail: '/account/packages/:packageId',
	accountPackageApprovePublish: '/account/packages/:packageId/approve-publish',
	accountPackageApprovePublishApi:
		'/account/packages/:packageId/approve-publish.json',
	accountPackageFiles: '/account/packages/:packageId/files(/*relativePath)',
	accountPackageFilesApi: '/account/packages/:packageId/files.json',
	accountPackagesApi: '/account/packages.json',
	accountPackagesApiPost: post('/account/packages.json'),
	accountSecrets: '/account/secrets',
	accountSecretNew: '/account/secrets/new',
	accountSecretUserDetail: '/account/secrets/user/:secretName',
	accountSecretSessionDetail: '/account/secrets/session/:sessionId/:secretName',
	accountSecretPackageDetail: '/account/secrets/package/:packageId/:secretName',
	accountSecretsApprove: '/account/secrets/approve',
	accountSecretsApi: '/account/secrets.json',
	accountSecretsApiPost: post('/account/secrets.json'),
	accountValues: '/account/values',
	accountValueNew: '/account/values/new',
	accountValueDetail: '/account/values/:valueId',
	accountValuesApi: '/account/values.json',
	accountValuesApiPost: post('/account/values.json'),
	accountJobs: '/account/jobs',
	accountJobDetail: '/account/jobs/:jobId',
	accountJobsApi: '/account/jobs.json',
	accountJobsApiPost: post('/account/jobs.json'),
	accountWorkflows: '/account/workflows',
	accountWorkflowDetail: '/account/workflows/:workflowId',
	accountWorkflowsApi: '/account/workflows.json',
	accountWorkflowsApiPost: post('/account/workflows.json'),
	// Thin cross-package index of declared webhooks. Each row deep-links into
	// the owning package's settings (`communityPackageSettings#webhooks`),
	// which is where mint / reveal / rotate / enable-disable live.
	accountWebhooks: '/account/webhooks',
	accountWebhooksApi: '/account/webhooks.json',
	accountActivity: '/account/activity',
	accountActivityDetail: '/account/activity/:runId',
	accountActivityApi: '/account/activity.json',
	accountWaiting: '/account/waiting',
	accountWaitingApi: '/account/waiting.json',
	accountShared: '/account/shared',
	accountSharedApi: '/account/shared.json',
	accountSharedApiPost: post('/account/shared.json'),
	accountMemories: '/account/memories',
	accountMemoryDetail: '/account/memories/:memoryId',
	// Sibling of `/account/memories.json` so `:memoryId` cannot claim the
	// signed-in user's memories download.
	accountMemoriesExport: '/account/memories-export.json',
	accountMemoriesApi: '/account/memories.json',
	accountMemoriesApiPost: post('/account/memories.json'),
	accountEmail: '/account/email',
	accountEmailDetail: '/account/email/:messageId',
	accountEmailApi: '/account/email.json',
	// The Connections page is the home for inbound MCP hosts (connected
	// agents): MCP URL, setup guides, and per-host revoke. Its data twin is
	// `/account/connected-agents.json`; `/account/connections.json` is the
	// older sign-in provider (GitHub, Google, …) list on the Overview page.
	accountConnections: '/account/connections',
	// Add connection: the full client grid, then one host's install steps.
	accountConnectionNew: '/account/connections/new',
	accountConnectionNewAgent: '/account/connections/new/:agent',
	accountConnectionsApi: '/account/connections.json',
	accountConnectionsApiPost: post('/account/connections.json'),
	accountConnectedAgentsApi: '/account/connected-agents.json',
	accountConnectedAgentsApiPost: post('/account/connected-agents.json'),
	accountPasskeys: '/account/passkeys',
	accountPasskeysApi: '/account/passkeys.json',
	accountPasskeysApiPost: post('/account/passkeys.json'),
	accountMcpOauthClients: '/account/mcp-oauth-clients',
	accountMcpOauthClientsApi: '/account/mcp-oauth-clients.json',
	accountMcpOauthClientsApiPost: post('/account/mcp-oauth-clients.json'),
	accountProfileApi: '/account/profile.json',
	accountProfileApiPost: post('/account/profile.json'),
	accountAvatarApiPost: post('/account/profile/avatar.json'),
	accountTwoFactor: '/account/two-factor',
	accountTwoFactorApi: '/account/two-factor.json',
	accountTwoFactorApiPost: post('/account/two-factor.json'),
	accountBilling: '/account/billing',
	accountBillingApi: '/account/billing.json',
	accountBillingCheckoutPost: post('/account/billing/checkout.json'),
	accountBillingCancellationFeedbackPost: post(
		'/account/billing/cancellation-feedback.json',
	),
	accountBillingSuccess: '/account/billing/success',
	accountBillingPortal: '/account/billing/portal',
	accountUsage: '/account/usage',
	accountUsageApi: '/account/usage.json',
	accountEmailChange: post('/account/email-change.json'),
	accountEmailClaimRelease: post('/account/email-claim-release.json'),
	accountPassword: post('/account/password.json'),
	accountResendVerification: post('/account/resend-verification.json'),
	accountExport: '/account/export.json',
	admin: '/admin',
	adminUsers: '/admin/users',
	adminUserDetail: '/admin/users/:stableUserId',
	adminUsersApi: '/admin/users.json',
	adminUsersApiPost: post('/admin/users.json'),
	adminRoles: '/admin/roles',
	adminRolesApi: '/admin/roles.json',
	adminReservedUsernames: '/admin/reserved-usernames',
	adminReservedUsernamesApi: '/admin/reserved-usernames.json',
	adminReservedUsernamesApiPost: post('/admin/reserved-usernames.json'),
	adminFeatureFlags: '/admin/feature-flags',
	adminFeatureFlagsApi: '/admin/feature-flags.json',
	adminFeatureFlagsApiPost: post('/admin/feature-flags.json'),
	adminBanners: '/admin/banners',
	adminBannersApi: '/admin/banners.json',
	adminBannersApiPost: post('/admin/banners.json'),
	siteBannerDismissPost: post('/site-banner-dismiss.json'),
	youtubeThumb: '/youtube-thumb/:videoId',
	adminPlatformIntegrations: '/admin/platform-integrations',
	adminPlatformIntegrationNew: '/admin/platform-integrations/new',
	adminPlatformIntegrationDetail: '/admin/platform-integrations/:slug',
	adminPlatformIntegrationsApi: '/admin/platform-integrations.json',
	adminPlatformIntegrationsApiPost: post('/admin/platform-integrations.json'),
	adminProviderMarks: '/admin/provider-marks',
	adminProviderMarksApi: '/admin/provider-marks.json',
	adminProviderMarksApiPost: post('/admin/provider-marks.json'),
	adminCodemods: '/admin/codemods',
	adminCodemodsApi: '/admin/codemods.json',
	adminCodemodsRunApi: post('/admin/codemods/run.json'),
	adminCodemodsRunStopApi: post('/admin/codemods/run/stop.json'),
	adminCommunityReports: '/admin/community-reports',
	adminCommunityReportsApi: '/admin/community-reports.json',
	adminCommunityReportsApiPost: post('/admin/community-reports.json'),
	adminUserUsageApi: '/admin/users/usage.json',
	adminInsights: '/admin/insights',
	adminInsightsApi: '/admin/insights.json',
	adminPlatformFeedback: '/admin/platform-feedback',
	adminPlatformFeedbackApi: '/admin/platform-feedback.json',
	adminSystemEmail: '/admin/system-email',
	adminSystemEmailApi: '/admin/system-email.json',
	blog: '/blog',
	blogApi: '/blog.json',
	blogRss: '/blog/rss.xml',
	blogPost: '/blog/:slug',
	blogPostApi: '/blog/:slug.json',
	blogPostMarkdown: '/blog/:slug.md',
	blogPostOgImage: '/blog/:slug/og.png',
	// `/docs` renders the introduction article (`what-is-kody`); the other
	// docs live at `/docs/:slug`. Every HTML page has `.md` and `.json` twins.
	docs: '/docs',
	docsApi: '/docs.json',
	docsMarkdown: '/docs.md',
	// Static companions must win over `/docs/:slug` so `connect` and
	// `llms.txt` are not treated as missing doc slugs.
	docsLlmsTxt: '/docs/llms.txt',
	docsConnect: '/docs/connect',
	docsConnectApi: '/docs/connect.json',
	docsConnectMarkdown: '/docs/connect.md',
	docDetail: '/docs/:slug',
	docDetailApi: '/docs/:slug.json',
	docDetailMarkdown: '/docs/:slug.md',
	docDetailOgImage: '/docs/:slug/og.png',
	llmsTxt: '/llms.txt',
	// The docs used to live under `/guides`. Everything there 308s to `/docs`
	// (see `#app/handlers/legacy-guides-redirect.ts`).
	legacyGuides: '/guides',
	legacyGuidesApi: '/guides.json',
	legacyGuidesMarkdown: '/guides.md',
	legacyGuidesPath: '/guides/*rest',
	community: '/community',
	communityApi: '/community.json',
	communityDetail: '/community/:listingId',
	communityDetailApi: '/community/:listingId.json',
	communityDetailFiles: '/community/:listingId/files(/*relativePath)',
	communityDetailFilesApi: '/community/:listingId/files.json',
	communityDetailRaw: '/community/:listingId/raw(/*relativePath)',
	communityDetailAsset: '/community/:listingId/assets(/*relativePath)',
	communityDetailIcon: '/community/:listingId/icon/:iconCommit',
	communityDetailOgImage: '/community/:listingId/og.png',
	communityReportApiPost: post('/community/:listingId/report.json'),
	communityTrustApiPost: post('/community/:listingId/trust.json'),
	communityFeatureApiPost: post('/community/:listingId/feature.json'),
	communityInstallApiPost: post('/community/:listingId/install.json'),
	profile: '/@:username',
	// Canonical public URL for a published package, keyed by its owner and
	// package name leaf (`/@kentcdodds/devin`) rather than the listing uuid. Deeper
	// package routes use a third-segment noun (`/files`) so they do not collide
	// with `/@:username/{packages,connectors,webhooks,api}` ingress.
	communityPackage: '/@:username/:kodyId',
	communityPackageFiles: '/@:username/:kodyId/files(/*relativePath)',
	communityPackageTree: '/@:username/:kodyId/tree/:ref(/*relativePath)',
	communityPackageRaw: '/@:username/:kodyId/raw/:ref(/*relativePath)',
	// First-party bytes for README-relative images. Third-segment noun so
	// `/@:username/packages/…` stays the hosted package-app mount.
	communityPackageAsset: '/@:username/:kodyId/assets(/*relativePath)',
	communityPackageSettings: '/@:username/:kodyId/settings',
	communityPackageApprovePublish: '/@:username/:kodyId/approve-publish',
	communityPackageApproveChanges: '/@:username/:kodyId/approve-changes',
	// JSON companion lives under `/profiles/…` with the other username-keyed
	// APIs, keeping the `/@…` namespace to human-shareable page URLs.
	communityPackageApi: '/profiles/:username/packages/:kodyId.json',
	communityPackageFilesApi: '/profiles/:username/packages/:kodyId/files.json',
	communityPackageApprovePublishApi:
		'/profiles/:username/packages/:kodyId/approve-publish.json',
	communityPackageApproveChangesApi:
		'/profiles/:username/packages/:kodyId/approve-changes.json',
	communityPackageShareApi: '/profiles/:username/packages/:kodyId/share.json',
	communityPackageShareApiPost: post(
		'/profiles/:username/packages/:kodyId/share.json',
	),
	// Owner-only webhook URL management for one package (the Webhooks section
	// of package settings): GET lists declared webhooks with minted state,
	// POST mints / rotates / reveals / enables / disables one. The credential
	// URL only ever leaves the server through this route's `reveal` intent,
	// never through MCP.
	communityPackageWebhooksApi:
		'/profiles/:username/packages/:kodyId/webhooks.json',
	communityPackageWebhooksApiPost: post(
		'/profiles/:username/packages/:kodyId/webhooks.json',
	),
	profileApi: '/profiles/:username.json',
	// `.` is a Remix route delimiter, so the filename must be `:hash.:ext`
	// (not a single `:cacheKey`) or `/avatar/abc.jpg` never matches.
	profileAvatar: '/profiles/:username/avatar/:hash.:ext',
	profileOgImage: '/profiles/:username/og.png',
	webhookIngress: post(
		'/@:username/webhooks/:packageKodyId/:webhookName/:urlSecret',
	),
	stripeWebhook: post('/webhooks/stripe'),
	health: '/health',
	healthComponents: '/health/components',
	sentryTunnel: post('/sentry-tunnel'),
	login: '/login',
	ogPageImage: '/og/:page.png',
	pricing: '/pricing',
	faq: '/faq',
	support: '/support',
	privacy: '/privacy',
	terms: '/terms',
	discord: '/discord',
	discordApi: '/discord.json',
	onboarding: '/onboarding',
	onboardingStep1: '/onboarding/step-1',
	onboardingStep1Agent: '/onboarding/step-1/:agent',
	onboardingStep2: '/onboarding/step-2',
	onboardingStep2Service: '/onboarding/step-2/:service',
	onboardingStep3: '/onboarding/step-3',
	onboardingStep3Agent: '/onboarding/step-3/:agent',
	onboardingApi: '/onboarding.json',
	onboardingChecklistDismissPost: post('/onboarding/checklist-dismiss.json'),
	resetPassword: '/reset-password',
	verify: '/verify',
	verifyTwoFactorApi: post('/verify/2fa.json'),
	verifyEmail: '/verify-email',
	verifyEmailChange: '/verify-email-change',
	verifyEmailClaimRelease: '/verify-email-claim-release',
	unsubscribeTips: '/unsubscribe/tips',
	pendingVerification: '/pending-verification',
	signup: '/signup',
	account: '/account',
	accountDelete: post('/account/delete'),
	auth: post('/auth'),
	authProvidersApi: '/auth/providers.json',
	authProviderStart: post('/auth/:provider'),
	authProviderCallback: '/auth/:provider/callback',
	session: '/session',
	logout: post('/logout'),
	passwordResetRequest: post('/password-reset'),
	passwordResetConfirm: post('/password-reset/confirm'),
	webauthnRegistration: '/webauthn/registration',
	webauthnRegistrationPost: post('/webauthn/registration'),
	webauthnAuthentication: '/webauthn/authentication',
	webauthnAuthenticationPost: post('/webauthn/authentication'),
})
